<template>
  <section>
    <div class="tw-mb-4 tw-flex tw-justify-end">
      <Button variant="outline" size="sm" @click="openSelfNotePopup">
        <Plus class="tw-h-4 tw-w-4" />
        {{ t('orders.titles.add_note') }}
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingNotes" class="tw-space-y-4">
      <div v-for="i in 2" :key="i" class="tw-flex tw-gap-3">
        <Skeleton class="tw-h-9 tw-w-9 tw-rounded-full tw-shrink-0" />
        <div class="tw-flex-1 tw-space-y-2">
          <Skeleton class="tw-h-4 tw-w-32" />
          <Skeleton class="tw-h-16 tw-w-full tw-rounded-xl" />
        </div>
      </div>
    </div>

    <!-- Notes (your private notes) -->
    <div v-else-if="selfNotes?.length" class="tw-space-y-4">
      <div
        v-for="(item, index) in selfNotes"
        :key="index"
        class="tw-group tw-flex tw-flex-row-reverse tw-items-end tw-gap-2.5"
      >
        <Avatar size="sm" shape="circle" class="tw-shrink-0">
          <AvatarImage
            v-if="item.author?.profile_picture"
            :src="item.author.profile_picture"
            :alt="item.author?.display_name"
          />
          <AvatarFallback
            class="tw-bg-primary-100 tw-text-[0.65rem] tw-font-semibold tw-text-primary-800 dark:tw-bg-primary-900/40 dark:tw-text-primary-200"
          >
            {{ getInitials(item.author?.display_name) }}
          </AvatarFallback>
        </Avatar>

        <div class="tw-flex tw-max-w-[82%] tw-flex-col tw-items-end">
          <div class="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-px-1">
            <div
              class="tw-flex tw-items-center tw-gap-0.5 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
            >
              <Button
                variant="ghost"
                size="icon"
                class="tw-h-7 tw-w-7 tw-text-surface-500 hover:tw-text-secondary-900"
                @click="OpenEditNotePopup(item)"
              >
                <Pencil class="tw-h-3.5 tw-w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                class="tw-h-7 tw-w-7 tw-text-surface-500 hover:tw-bg-red-50 hover:tw-text-red-600 dark:hover:tw-bg-red-950/30"
                @click="openDeleteNoteDialog(item)"
              >
                <Trash2 class="tw-h-3.5 tw-w-3.5" />
              </Button>
            </div>
            <span
              class="tw-text-xs tw-font-semibold tw-text-secondary-700 dark:tw-text-neutral-300"
            >
              {{ item.author?.display_name }}
            </span>
            <span class="tw-text-[0.7rem] tw-text-surface-400 dark:tw-text-neutral-500">
              {{ moment(item.created_on).format('DD.MM.YYYY') }}
            </span>
          </div>
          <div
            class="tw-whitespace-pre-wrap tw-break-words tw-rounded-2xl tw-rounded-br-md tw-border tw-border-primary-200 tw-bg-primary-50 tw-px-3.5 tw-py-2.5 tw-text-sm tw-leading-relaxed tw-text-secondary-800 dark:tw-border-primary-900/40 dark:tw-bg-primary-900/20 dark:tw-text-neutral-100"
          >
            {{ item.text }}
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-surface-200 tw-py-10 tw-text-center dark:tw-border-neutral-800"
    >
      <StickyNote class="tw-h-11 tw-w-11 tw-text-surface-300 dark:tw-text-neutral-600" />
      <p class="tw-mt-3 tw-text-sm tw-font-medium tw-text-surface-500 dark:tw-text-neutral-400">
        {{ t('orders.titles.no_self_notes') }}
      </p>
    </div>
  </section>

  <!-- Add/Edit Note Dialog -->
  <Dialog v-model:open="isNotesPopup">
    <DialogContent class="sm:tw-max-w-md">
      <DialogHeader>
        <DialogTitle>
          {{ isEditNote ? t('orders.titles.edit_note') : t('orders.titles.add_self_note') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('orders.titles.self_note_dialog_description') }}
        </DialogDescription>
      </DialogHeader>
      <form @submit.prevent="isEditNote ? editNote() : addSelfNote()">
        <Textarea
          v-model="selfNote"
          :placeholder="t('orders.titles.self_note_hint')"
          class="tw-min-h-[150px]"
        />
        <DialogFooter class="tw-mt-4">
          <Button type="button" variant="outline" @click="closePopup">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button type="submit" :disabled="!selfNote.trim()">
            {{ isEditNote ? t('orders.titles.edit_note') : t('orders.titles.add_note') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>

  <!-- Delete Confirmation Dialog -->
  <Dialog v-model:open="isNoteDeletePopup">
    <DialogContent class="sm:tw-max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
        <DialogDescription>{{ t('orders.titles.confirm_delete') }}?</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="closePopup">
          {{ t('common.forms.cancel') }}
        </Button>
        <Button variant="destructive" @click="onChangeNoteDelete">
          {{ t('common.forms.delete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import moment from 'moment';
import { useOrderStore } from '@/stores/order.store';
import { useToast } from 'vue-toast-notification';
import { useRouter } from 'vue-router';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

// UI Components
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Icons
import { Plus, Pencil, Trash2, StickyNote } from 'lucide-vue-next';

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const toast = useToast();
const router = useRouter();

const { getOrderNotes, createOrderNotes, editOrderNotes, deleteOrderNotes } = useOrderStore();
const {
  getRecurringInstructionSelfNotes,
  createRecurringInstructionSelfNote,
  editRecurringInstructionSelfNote,
  deleteRecurringInstructionSelfNote,
} = useRecurringInstructionStore();

const props = defineProps({
  order: {
    type: Object,
    default: () => ({}),
  },
  recurringInstruction: {
    type: Object,
    default: () => ({}),
  },
});

const isNotesPopup = ref(false);
const orderId = ref<number | null>(null);
const isLoadingNotes = ref(false);
const selfNotes = ref<any[]>([]);
const isNoteDeletePopup = ref(false);
const isEditNote = ref(false);
const selfNote = ref('');
const noteId = ref<number | null>(null);

const isRecurringInstruction = () => {
  return props.recurringInstruction?.id !== undefined;
};

const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const openSelfNotePopup = () => {
  isEditNote.value = false;
  selfNote.value = '';
  isNotesPopup.value = true;
};

const closePopup = () => {
  isNotesPopup.value = false;
  isNoteDeletePopup.value = false;
};

const getSelfNotes = async () => {
  isLoadingNotes.value = true;
  try {
    if (isRecurringInstruction()) {
      selfNotes.value = await getRecurringInstructionSelfNotes(orderId.value!);
    } else {
      const response = await getOrderNotes(orderId.value!);
      selfNotes.value = response.results;
    }
  } finally {
    isLoadingNotes.value = false;
  }
};

const OpenEditNotePopup = (note: any) => {
  isEditNote.value = true;
  isNotesPopup.value = true;
  selfNote.value = note.text;
  noteId.value = note.id;
};

const openDeleteNoteDialog = (note: any) => {
  isNoteDeletePopup.value = true;
  noteId.value = note.id;
};

const addSelfNote = async () => {
  if (selfNote.value.trim() === '') return;

  if (isRecurringInstruction()) {
    await handleApiError(async () => {
      await createRecurringInstructionSelfNote(orderId.value!, selfNote.value);
      await getSelfNotes();
    }, t('requests.titles.successful_self_note_created'));
    selfNote.value = '';
    isNotesPopup.value = false;
    return;
  }

  const obj = {
    order: orderId.value,
    text: selfNote.value,
  };
  await handleApiError(async () => {
    await createOrderNotes(obj);
    await getSelfNotes();
  }, t('requests.titles.successful_self_note_created'));
  selfNote.value = '';
  isNotesPopup.value = false;
};

const editNote = async () => {
  if (selfNote.value.trim() === '') return;

  if (isRecurringInstruction()) {
    await editRecurringInstructionSelfNote(orderId.value!, noteId.value!, selfNote.value).then(
      async () => {
        toast.open({
          message: t('requests.titles.successful_self_note_edited'),
          type: 'success',
        });
        await getSelfNotes();
      }
    );
    isNotesPopup.value = false;
    isEditNote.value = false;
    selfNote.value = '';
    return;
  }

  await editOrderNotes(noteId.value!, {
    order: orderId.value,
    text: selfNote.value,
  }).then(async () => {
    toast.open({
      message: t('requests.titles.successful_self_note_edited'),
      type: 'success',
    });
    await getSelfNotes();
  });
  isNotesPopup.value = false;
  isEditNote.value = false;
  selfNote.value = '';
};

const onChangeNoteDelete = async () => {
  closePopup();

  if (isRecurringInstruction()) {
    await deleteRecurringInstructionSelfNote(orderId.value!, noteId.value!)
      .then(async () => {
        toast.open({
          message: t('requests.titles.successful_self_note_deleted'),
          type: 'success',
        });
        await getSelfNotes();
      })
      .catch(error => console.log(error));
    return;
  }

  await deleteOrderNotes(noteId.value!)
    .then(async () => {
      toast.open({
        message: t('requests.titles.successful_self_note_deleted'),
        type: 'success',
      });
      await getSelfNotes();
    })
    .catch(error => console.log(error));
};

onMounted(async () => {
  orderId.value = +router.currentRoute.value.params.id;
  await getSelfNotes();
});
</script>
